import React from "react";
import { Dialog } from "primereact/dialog";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import useResolveGiftRecipientByEmail from "api/BookResource/useResolveGiftRecipientByEmail";
import useGiftBook from "api/BookResource/useGiftBook";

const GiftBookModal = ({ visible, onHide, selectedBook }) => {
	const [recipientQuery, setRecipientQuery] = React.useState("");
	const [giftMessage, setGiftMessage] = React.useState("");
	const [recipient, setRecipient] = React.useState(null);
	const [inlineError, setInlineError] = React.useState(null);

	const { mutateAsync: resolveRecipient, isLoading: isResolvingRecipient } =
		useResolveGiftRecipientByEmail();
	const { mutateAsync: giftBook, isLoading: isGifting } = useGiftBook();

	React.useEffect(() => {
		if (!visible) {
			setRecipientQuery("");
			setGiftMessage("");
			setRecipient(null);
			setInlineError(null);
		}
	}, [visible]);

	const handleLookup = async () => {
		setInlineError(null);
		if (!recipientQuery?.trim()) {
			setInlineError("Enter recipient email or username first.");
			setRecipient(null);
			return;
		}

		const resolved = await resolveRecipient(recipientQuery.trim());
		if (!resolved) {
			setRecipient(null);
			setInlineError("No user found with that email or username.");
			return;
		}

		setRecipient(resolved);
	};

	const handleGift = async () => {
		setInlineError(null);
		if (!selectedBook?.id) {
			return;
		}

		if (!recipient?.id) {
			setInlineError("Find and confirm recipient before sending the gift.");
			return;
		}

		await giftBook({
			bookId: selectedBook.id,
			recipientQuery: recipientQuery.trim(),
			giftMessage: giftMessage.trim() || null,
			recipientUserId: recipient.id,
		});

		onHide();
	};

	const sending = isGifting || isResolvingRecipient;

	return (
		<Dialog
			visible={visible}
			onHide={onHide}
			header={`Gift: ${selectedBook?.title || "Book"}`}
			style={{ width: "min(92vw, 640px)" }}
		>
			<div className="space-y-4 py-1">
				<div>
					<label htmlFor="gift-recipient-email" className="text-sm font-medium text-gray-700">
						Recipient email or username
					</label>
					<div className="mt-2 flex gap-2">
						<input
							id="gift-recipient-email"
							type="text"
							value={recipientQuery}
							onChange={(e) => {
								setRecipientQuery(e.target.value);
								setRecipient(null);
								setInlineError(null);
							}}
							placeholder="name@example.com or username"
							className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-akgreener focus:outline-none focus:ring-1 focus:ring-akgreener"
						/>
						<button
							type="button"
							onClick={handleLookup}
							disabled={sending || !recipientQuery?.trim()}
							className="rounded-lg border border-akgreener px-3 py-2 text-sm font-medium text-akgreenest disabled:cursor-not-allowed disabled:opacity-50"
						>
							{isResolvingRecipient ? "Finding..." : "Find"}
						</button>
					</div>
				</div>

				{recipient && (
					<div className="rounded-lg border border-green-50 bg-green-50 px-3 py-2 text-sm text-green-900">
						<div className="font-semibold">Recipient found</div>
						<div>
							{recipient?.first_name || ""} {recipient?.last_name || ""}
						</div>
						<div className="text-xs">{recipient?.email}</div>
					</div>
				)}

				<div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
					<div className="flex items-start gap-2">
						<svg
							className="w-5 h-5 flex-shrink-0 mt-0.5"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
							/>
						</svg>
						<div>
							<div className="font-semibold">Important</div>
							<div>
								After gifting, you will lose access to this book. The book will be transferred to
								the recipient.
							</div>
						</div>
					</div>
				</div>

				<div>
					<label htmlFor="gift-message" className="text-sm font-medium text-gray-700">
						Gift message (optional)
					</label>
					<textarea
						id="gift-message"
						value={giftMessage}
						onChange={(e) => setGiftMessage(e.target.value)}
						rows={4}
						maxLength={500}
						placeholder="Enjoy this book!"
						className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-akgreener focus:outline-none focus:ring-1 focus:ring-akgreener"
					/>
					<p className="mt-1 text-right text-xs text-gray-500">{giftMessage.length}/500</p>
				</div>

				{inlineError && <p className="text-sm text-red-600">{inlineError}</p>}

				<div className="flex justify-end gap-2 pt-2">
					<button
						type="button"
						onClick={onHide}
						disabled={sending}
						className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 disabled:opacity-50"
					>
						Cancel
					</button>
					<button
						type="button"
						onClick={handleGift}
						disabled={sending || !recipient?.id}
						className="rounded-lg bg-akgreener px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
					>
						{isGifting ? (
							<span className="inline-flex items-center gap-2">
								<AiOutlineLoading3Quarters className="h-4 w-4 animate-spin" /> Sending...
							</span>
						) : (
							"Gift & Transfer"
						)}
					</button>
				</div>
			</div>
		</Dialog>
	);
};

export default GiftBookModal;
